import React, { useEffect, useRef, useState } from 'react';
import './prize-packs-form.styles.scss';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import ManageContentInformation from '../blocks/information.component';
import ManageContentIconCropped from '../blocks/icon.component';
import ManageContentCategories from '../blocks/categories.component';
import ManageContentPrerequisites from '../blocks/prerequisites.component';
import ManageContentMarket from '../blocks/market.component';
import ManageContentPrizePackItems from '../blocks/prize-pack-items.component';
import ManageContentPrizePackWeights from '../blocks/prize-pack-weights.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { useSelector } from 'react-redux';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { RandomString } from '@/utils/randomString';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ManageContentDates from '../blocks/dates.component';
import PrizePackCount from '../blocks/prize-pack-count.component';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import ElementDelete from '@/components/SHARED/element-delete/element-delete.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedBlob } from '@/utils/getOptimizedBlob';

const PrizePacksForm = ({
  gameData,
  gameStats,
  gameElements,
  prizepacksFromDb,
  playerList,
  role
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const membership = useSelector(state => state.userData.membership);
  const [formData, setFormData] = useState({});
  const [categories, setCategories] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [teams, setTeams] = useState([]);
  const { packPath } = useParams();
  const { trackEvent, trackException } = useAnalytics();
  const navigate = useNavigate();
  const storage = getStorage();
  const dataRef = useRef();
  const approveRef = useRef();
  const accessDeniedRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (!role || !gameData?.path) return;
    if (role !== 'manager' && role !== 'owner') {
      setAlertMessage('You do not have permission to create or edit prize packs.');
      accessDeniedRef.current = () => navigate(`/manage/${gameData.path}/elements/prizepacks`);
    }
  }, [role, gameData?.path, navigate])

  useEffect(() => {
    if (!prizepacksFromDb || !packPath) {
      dataRef.current = {
        'opt_visible': true,
        'opt_available': true,
        'opt_preview': true
      };
      setFormData(dataRef.current);
      return;
    };
    const packToEdit = Object.values(prizepacksFromDb)
      .filter(p => p.path === packPath)[0];
    dataRef.current = { ...packToEdit };
    setFormData(dataRef.current);
  }, [prizepacksFromDb, packPath])

  useEffect(() => {
    if (!gameElements) return;
    if (prizepacksFromDb) {
      let arr = [];
      for (let c of Object.values(prizepacksFromDb).filter(c => c.opt_itemType === 'prizepack')) {
        if (c.opt_cat && !arr.includes(c.opt_cat)) {
          arr.push(c.opt_cat)
        }
      }
      setCategories(arr.sort())
    }
    const avail = Object.values(gameElements)
      .filter(e =>
        e.type === 'item' &&
        e.opt_itemType !== 'contribution' &&
        e.opt_itemType !== 'prizepack'
      )
    setAvailableItems([...avail])
  }, [gameElements, prizepacksFromDb])

  useEffect(() => {
    if (!playerList) return;
    const teamList = [...new Set(
      Object.values(playerList)
        .filter(p => p.team && p.status === 'active')
        .sort((a, b) => a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1 : -1)
    )];
    setTeams(teamList);
  }, [playerList])

  function saveData(key, value) {
    if (key === 'opt_prizes') {
      const oldPrizes = formData.opt_prizes ? [...formData.opt_prizes] : [];
      updateWeights(oldPrizes, value);
    }
    dataRef.current = { ...dataRef.current, ...{ [key]: value } };
    setFormData(dataRef.current);
  }

  function updateWeights(oldPrizes, chosenPrizes) {
    let weights = formData.opt_prizeWeights ? { ...formData.opt_prizeWeights } : {};
    for (let old of oldPrizes) {
      if (!chosenPrizes.includes(old)) {
        delete weights[old];
      }
    }
    for (let p of chosenPrizes) {
      if (!weights[p]) {
        weights[p] = {
          'id': p,
          'weight': 20
        }
      }
    }
    saveData('opt_prizeWeights', weights);
  }

  function cancelCreate() {
    setConfirmMessage('Are you sure you want to cancel? All of your data will be lost.');
    approveRef.current = () => {
      dataRef.current = {};
      setFormData(dataRef.current);
      navigate(-1)
    }
  }

  function handleSubmit() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    let tempData = { ...dataRef.current };
    if (tempData.opt_market && !tempData.opt_marketPrice) {
      setAlertMessage('You must assign a Market Price if you want the Prize Pack to be available in the Store.');
      return;
    }
    if (!tempData.opt_prizes || tempData.opt_prizes.length === 0) {
      setAlertMessage('You must select at least one item to be included in the Prize Pack.');
      return;
    }
    if (tempData.opt_prizes.length !== Object.keys(tempData.opt_prizeWeights).length) {
      setAlertMessage('You must assign a weight to each item in the Prize Pack.');
      return;
    }
    if (!tempData.name || !tempData.icon) {
      setAlertMessage('You must enter a name and choose an icon for the Prize Pack.');
      return;
    }
    if (!tempData.opt_itemCount || tempData.opt_itemCount < 1) {
      setAlertMessage('You must enter a number of items that will be included in the Prize Pack.');
      return;
    }
    if (
      gameElements &&
      Object.values(gameElements).filter(e =>
        e.name.trim().toLowerCase() === tempData.name.trim().toLowerCase() &&
        e.id !== tempData.id
      ).length > 0
    ) {
      setAlertMessage('There is already a Prize Pack with this name. ' +
        'Please choose a different name for this prize pack.');
      setLoading(false);
      return;
    }
    setConfirmMessage('Are you sure you are ready to save this Prize Pack? All of these settings can be changed later.')
    approveRef.current = () => {
      finishApproval()
    }
  }

  async function finishApproval() {
    setLoading(true);
    let tempData = { ...dataRef.current }
    if (tempData.icon.upload) {
      // tempData.icon.croppie.result({
      // 	type:'blob',
      // 	size: {'width': 352, 'height': 352},
      //     quality: 0.7,
      // })
      // .then((blob) => {
      // 	uploadImage(tempData.icon, blob)
      // 	return;
      // })
      const optimizedBlob = await getOptimizedBlob(tempData.icon.croppie, 25);
      console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
      uploadImage(tempData.icon, optimizedBlob);
    } else if (tempData.icon.url) {
      savePrizePack(tempData.icon.url)
      return;
    } else if (tempData.icon) {
      savePrizePack(tempData.icon)
      return;
    } else {
      setAlertMessage('Something went wrong with the icon. Please try using a different image.');
      setLoading(false);
      return;
    }
  }

  function uploadImage(icon, blob) {
    const now = new Date().getTime();
    const path = `${userProfile.appId}/${now}-${icon.name}`;
    const iconRef = ref(storage, path);
    const metadata = {
      contentType: icon.type,
      customMetadata: {
        appId: userProfile.appId,
        email: userProfile.email
      }
    };
    uploadBytes(iconRef, blob, metadata).then((snapshot) => {
      getDownloadURL(iconRef)
        .then(async (url) => {
          savePrizePack(url);
          try {
            await UseCloudFunction(
              'saveToMyContent',
              {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'source': 'Prize Pack Icons',
                'category': 'Images',
                'cropped': true,
                'contentData': {
                  'name': icon.name,
                  'url': url,
                  'type': 'image',
                  'id': `${now}_${RandomString(10)}_image`
                },
              }
            )
          } catch (error) {
            console.log('Error saving to My Content: ', error);
            setAlertMessage('Something went wrong while saving the icon to My Content. Error: ' + error.message);
            trackException('Prize Pack Icon Save to My Content Error: ' + error.message);
          }
          trackEvent(
            'image_saved_to_my_content',
            'Game Manager',
            `Prize Pack icon ${icon.name} was uploaded for user ${userProfile.appId}.`,
            null,
            membership
          )
          return;
        })
    })
  }

  async function savePrizePack(iconUrl) {
    let tempData = { ...dataRef.current };
    const now = new Date().getTime();
    const gameCode = gameData.gameId.substring(
      gameData.gameId.indexOf('-') - 5, 
      gameData.gameId.indexOf('-') + 6
    )
    let action = "Edited";
    if (!tempData.id) {
      tempData.appId = userProfile.appId;
      tempData.gameId = gameData.gameId;
      tempData.id = now + '-' + gameCode + '-prizepack';
      tempData.status = 'active';
      tempData.type = 'item';
      tempData.opt_itemType = 'prizepack';
      tempData.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz-_0123456789');
      action = "Created";
    }
    tempData.ts = now;
    tempData.icon = iconUrl;
    console.log(tempData);
    const res = await UseCloudFunction(
      'saveGameElement',
      {
        'gameId': gameData.gameId,
        'appId': userProfile.appId,
        'elementData': tempData,
        'userName': `${userProfile.fName} ${userProfile.lName}`,
        'action': action
      }
    )
    console.log(res);
    if (res.error) {
      setAlertMessage('Something went wrong. Please try again later. Error: ' + res.error);
      setLoading(false);
      trackException(`Prize Pack ${action} Error: ${res.error}`);
      return;
    }
    setAlertMessage('Prize Pack saved successfully.');
    dataRef.current = {};
    setFormData(dataRef.current);
    setLoading(false);
    const event = action === "Created" ? 'prize_pack_created' : 'prize_pack_edited';
    trackEvent(
      event,
      'Game Manager',
      `Prize Pack ${tempData.id} was ${action.toLowerCase()}.`,
      null,
      membership
    )
    navigate(-1);
  }

  return (
    <div className={`prize-packs-form ${screenSize}`}>
      <div className='g-card prize-packs-card'>
        <BackButton cancel={() => navigate(-1)} />
        <div className="g-space-1"></div>
        <div className='card-title'>
          <ImageContainer src='/images/icons/prizepacks.png' alt='items icon' className='head-icon' />
          Create and Edit Prize Packs
        </div>
        <hr />
        <div className='required meta'>* = Required</div>
        <div className="g-space-0-5"></div>
        <div className='create-item-form'>
          <ManageContentInformation
            type='prizePack'
            saveData={saveData}
            formData={formData}
            textEditorLabel='Prize Pack Description (optional)'
          />
          <div className='g-space-2'></div>
          <ManageContentIconCropped
            type='prizePack'
            saveData={saveData}
            formData={formData}
          />
          <div className='g-space-2'></div>
          <ManageContentPrizePackItems
            formData={formData}
            saveData={saveData}
            gameData={gameData}
            gameStats={gameStats}
            gameElements={availableItems} />
          <div className='g-space-2'></div>
          <ManageContentPrizePackWeights
            formData={formData}
            saveData={saveData}
            gameElements={gameElements} />
          <div className='g-space-2'></div>
          <PrizePackCount
            formData={formData}
            saveData={saveData} />
          <div className='g-space-1'></div>
          <ShowHideSettingsButton
            setShowAdvancedOptions={setShowAdvancedOptions}
            showAdvancedOptions={showAdvancedOptions} />
          <div className={`advanced-options ${showAdvancedOptions ? 'show' : 'hide'}`}>
            <div className='g-space-1'></div>

            <ManageContentCategories
              type='prizePack'
              saveData={saveData}
              formData={formData}
              categories={categories} />
            <div className='g-space-2'></div>
            <ManageContentVisibility
              type='prizePack'
              saveData={saveData}
              formData={formData}
              visibilityText="Turn visibility ON for this Prize Pack. If toggled off, it will be totally hidden from players."
              previewText='Allow players to see this item in their Inventory before they earned it. (If toggled off, players will see a mystery item.)'
              availabilityText='This Prize Pack is able to be earned via manager distribution. This Item is also able to be purchased if included in the Marketplace.' />
            <div className='g-space-2'></div>
            <ManageContentPrerequisites
              type='prizePack'
              saveData={saveData}
              formData={formData}
              gameElements={gameElements}
              availableItems={availableItems}
              teams={teams}
              gameData={gameData}
              gameStats={gameStats} />
            <div className='g-space-2'></div>
            <ManageContentMarket
              type='prizePack'
              formData={formData}
              saveData={saveData}
              gameElements={gameElements} />
            <div className='g-space-2'></div>
            <ManageContentDates
              type='prizePack'
              saveData={saveData}
              formData={formData}
              gameElements={gameElements}
              text={`Prize Packs will only be available to purchase before or after these dates.`} />
            <div className="g-space-1"></div>
            <ShowHideSettingsButton
              setShowAdvancedOptions={setShowAdvancedOptions}
              showAdvancedOptions={showAdvancedOptions}
              below={true} />
          </div>
        </div>
        <div className='g-space-1'></div>
        {/* {
                    (loading) 
                    ?
                    <div className='buttons'>
                        <button className='g-button '>
                            Cancel
                        </button>
                        <button className='g-button'>
                            <ThreeDotsLoader />
                        </button>
                    </div>
                    :
                    <div className='buttons'>
                        <button 
                            className='g-button'
                            onClick={() => navigate(-1)}>
                            Cancel
                        </button>
                        <button
                            className={`g-button primary ${
                                !formData.name ||
                                !formData.icon ||
                                !formData.opt_itemCount ||
                                (
                                    !formData.opt_prizes ||
                                    formData.opt_prizes.length === 0
                                )
                                ?
                                'disabled'
                                :
                                ''
                            }`}
                            onClick={() => handleSubmit()}>
                            Save Prize Pack
                        </button>
                    </div>
                } */}
        <div className='buttons'>
          {
            (loading)
              ?
              <>
                {
                  (formData.ts_created) &&
                  <button
                    type='button'
                    className={`g-button small-btn red ${screenSize !== 'mobile' ? 'left-item' : ''}`}>
                    Delete
                  </button>
                }
                <button
                  type='button'
                  className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                  Cancel
                </button>
                <button
                  type='button'
                  className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                  <ThreeDotsLoader />
                </button>
              </>
              :
              <>
                {
                  (formData.ts_created) &&
                  <ElementDelete
                    element={formData}
                    style='button'
                    classText={screenSize !== 'mobile' ? 'left-item' : ''} />
                }
                <button
                  type='button'
                  className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                  onClick={() => cancelCreate()}>
                  Cancel
                </button>
                <button
                  type='button'
                  className={`g-button submit-button`}
                  onClick={() => handleSubmit()}>
                  Save Prize Pack
                </button>
              </>
          }
        </div>
      </div>
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={accessDeniedRef.current} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <FullPageLoader
        show={loading} />
    </div>
  )
}

export default PrizePacksForm